import React, { Suspense, useCallback, useEffect, useState } from "react";
import { getEducations } from "./Models/firebaseHelper";
import { Route, Routes, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Slide, ToastContainer } from "react-toastify";
import LoadingScreen from "./Components/LoadingScreen";
import Menu from "./UseCases/Menu/Menu";
import AppShellHeader from "./UseCases/Menu/AppShellHeader";
import { MenuProvider } from "./UseCases/Menu/MenuContext";
import { useDockedSidebar } from "./hooks/useDockedSidebar";
import { EducationProvider } from "./UseCases/Educations/EducationContext";
import useLogin from "./hooks/useLogin";
import { useUser } from "./UseCases/User/UserObject";
import { setSuperProperties } from "./util/mixpanel";
import {
  Routes as RouteNames,
  RouteNames as RouteStrings,
  isUiElementsPath,
} from "./Routes";
import { IS_UI_ELEMENTS_ENABLED } from "./constants";
import { checkLanguage, setupLanguage } from "./util/language";
import { Course } from "./UseCases/Menu/Menu.types";
import { keys } from "./strings";
import EducationCourseLayout from "./UseCases/Educations/EducationCourseLayout";
import ErrorMessage from "./Components/ErrorMessage/ErrorMessage";
import { useTranslation } from "react-i18next";

import style from "./App.module.css";
import { preloadEducationRouteModules } from "./preloadEducationRouteModules";

// The lazy loaded components here reduces the bundle size significantly
// and improves the initial load time of the app.
const Account = React.lazy(() => import("./pages/Account/Account"));
const Admin = React.lazy(() => import("./UseCases/Admin/Admin"));
const AddEmployeesView = React.lazy(
  () => import("./UseCases/Admin/AddEmployees")
);
const NotFound = React.lazy(() => import("./UseCases/NotFound"));
const Login = React.lazy(() => import("./UseCases/Login/Login"));
const Education = React.lazy(() => import("./UseCases/Educations/Education"));
const EducationPage = React.lazy(() => import("./pages/Education"));
const QuestionsPractice = React.lazy(
  () => import("./UseCases/Questions/QuestionsPractice")
);
const TheoryList = React.lazy(() => import("./UseCases/Theory/TheoryList"));
const PracticeExam = React.lazy(
  () => import("./UseCases/Practice/PracticeExam")
);
const CertificatePage = React.lazy(
  () => import("./pages/CertificatePage")
);
const DocumentsList = React.lazy(
  () => import("./UseCases/Documents/DocumentsList")
);
const Welcome = React.lazy(() => import("./pages/Welcome/Welcome"));
const MyCertificates = React.lazy(
  () => import("./pages/MyCertificates/MyCertificates")
);
const ReferralLanding = React.lazy(
  () => import("./pages/ReferralLanding/ReferralLanding")
);
const UIElements = IS_UI_ELEMENTS_ENABLED
  ? React.lazy(() => import("./pages/UIElements/UIElements"))
  : null;

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const isLoggedIn = useLogin();
  const isDockedSidebar = useDockedSidebar();
  const isUiElementsPage = isUiElementsPath(location.pathname);
  const showAppHeader =
    Boolean(isLoggedIn) && isDockedSidebar && !isUiElementsPage;

  useEffect(() => {
    if (showAppHeader) {
      document.documentElement.style.setProperty(
        "--app-sidebar-current-width",
        "0px",
      );
    }
  }, [showAppHeader]);
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(true);
  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [coursesError, setCoursesError] = useState<string | null>(null);
  const [certificateRefreshKey, setCertificateRefreshKey] = useState<number>(0);
  const { user, userLoading, userError, updateUser, refreshUser } = useUser();

  useEffect(() => {
    setSuperProperties({
      environment: process.env.NODE_ENV,
      source: "webApp",
      url: "https://finans-utbildarna.web.app",
      // appVersion: "2.1.0", // TODO
    });
  }, []);

  // Set Swedish as default language for non-logged in users
  useEffect(() => {
    if (!isLoggedIn && !localStorage.getItem(keys.localStorage.language)) {
      setupLanguage("sv");
    }
  }, [isLoggedIn]);

  // Update on login status change
  useEffect(() => {
    if (isLoggedIn === null) {
      return;
    }

    setLoading(false);

    if (!isLoggedIn) {
      setSelectedCourse(null);
    }
  }, [isLoggedIn, setSelectedCourse]);

  const handleSelectCourse = useCallback(
    (course: Course) => {
      setSelectedCourse(course);
      localStorage.setItem(
        keys.localStorage.selectedCourse,
        JSON.stringify(course)
      );
    },
    [setSelectedCourse]
  );

  const fetchCourses = useCallback(
    async (retryCount = 0) => {
      const maxRetries = 3;
      const retryDelay = 1000 * Math.pow(2, retryCount); // Exponential backoff

      try {
        setCoursesError(null);
        const availableCourses = (await getEducations()) as Course[];
        setCourses(availableCourses);
      } catch (error) {
        console.error(
          `Failed to fetch courses (attempt ${retryCount + 1}):`,
          error
        );

        if (retryCount < maxRetries) {
          console.log(`Retrying in ${retryDelay}ms...`);
          setTimeout(() => {
            fetchCourses(retryCount + 1);
          }, retryDelay);
        } else {
          console.error("Max retries reached. Courses could not be loaded.");
          setCoursesError(t("error.coursesLoadFailed"));
          setCourses([]);
        }
      }
    },
    [setCourses, setCoursesError, t]
  );

  const retryFetchCourses = useCallback(() => {
    fetchCourses(0);
  }, [fetchCourses]);

  // Load selected course from localStorage on initial load
  useEffect(() => {
    const savedCourse = localStorage.getItem(keys.localStorage.selectedCourse);

    if (!selectedCourse && savedCourse) {
      const course = JSON.parse(savedCourse);
      setSelectedCourse(course);
      return;
    }

    // If selectedCourse is set but localStorage doesn't have it, clear selectedCourse
    // This handles the case where localStorage was cleared (e.g., after reassignment)
    if (selectedCourse && !savedCourse) {
      setSelectedCourse(null);
    }
  }, [selectedCourse, setSelectedCourse]);

  // Validate and update selected course with latest data from Firebase
  useEffect(() => {
    if (!isLoggedIn || !user || courses.length === 0) {
      return;
    }

    const savedCourse = localStorage.getItem(keys.localStorage.selectedCourse);
    if (!savedCourse) {
      return;
    }

    try {
      const parsedCourse = JSON.parse(savedCourse) as Course;

      // Check if course exists in available courses
      const courseExists = courses.find((c) => c.ID === parsedCourse.ID);
      if (!courseExists) {
        // Course no longer exists, remove from localStorage
        localStorage.removeItem(keys.localStorage.selectedCourse);
        if (selectedCourse?.ID === parsedCourse.ID) {
          setSelectedCourse(null);
        }
        return;
      }

      // Check if user has access to this course
      const userEducation = user.Educations?.find(
        (edu) => edu.ID === parsedCourse.ID
      );

      if (!userEducation) {
        // User doesn't have this education, remove from localStorage
        localStorage.removeItem(keys.localStorage.selectedCourse);
        if (selectedCourse?.ID === parsedCourse.ID) {
          setSelectedCourse(null);
        }
        return;
      }

      const updatedCourse = {
        ...courseExists,
        ...userEducation,
        PublishedStatus: courseExists.PublishedStatus,
        Title: courseExists.Title,
      };

      const updatedJson = JSON.stringify(updatedCourse);
      if (updatedJson !== savedCourse) {
        setSelectedCourse(updatedCourse);
        localStorage.setItem(
          keys.localStorage.selectedCourse,
          updatedJson
        );
      } else if (!selectedCourse || selectedCourse.ID !== updatedCourse.ID) {
        setSelectedCourse(updatedCourse);
      }
    } catch (error) {
      // Invalid JSON in localStorage, remove it
      console.error("Error parsing saved course from localStorage:", error);
      localStorage.removeItem(keys.localStorage.selectedCourse);
      if (selectedCourse) {
        setSelectedCourse(null);
      }
    }
    // Only run when user or courses change, not when selectedCourse changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, user, courses]);

  // Listen for custom event to clear selectedCourse immediately
  useEffect(() => {
    const handleClearSelectedCourse = () => {
      setSelectedCourse(null);
    };

    window.addEventListener("clearSelectedCourse", handleClearSelectedCourse);
    return () =>
      window.removeEventListener(
        "clearSelectedCourse",
        handleClearSelectedCourse
      );
  }, [setSelectedCourse]);

  useEffect(() => {
    if (isLoggedIn) {
      fetchCourses();
    }
  }, [fetchCourses, isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn || !selectedCourse?.ID) {
      return;
    }
    preloadEducationRouteModules();
  }, [isLoggedIn, selectedCourse?.ID]);

  useEffect(() => {
    if (isLoggedIn) {
      checkLanguage(getEducations);
    }
  }, [isLoggedIn]);

  /**
   * Handler for tab change in the SlidingTabBar
   * @param item - The selected tab item
   */
  const onTabChange = useCallback(
    (item: TabItem) => {
      navigate(item.to);
    },
    [navigate]
  );

  /**
   * Handler for when a certificate is generated
   */
  const handleCertificateGenerated = useCallback(() => {
    setCertificateRefreshKey((prev) => prev + 1);
    window.dispatchEvent(
      new CustomEvent("refreshMenuCourses", { detail: { silent: true } }),
    );
    refreshUser();
  }, [refreshUser]);

  const EducationRoutes = (
    <Routes>
      <Route
        element={
          <EducationCourseLayout
            courses={courses}
            selectedCourse={selectedCourse}
            userData={user}
            certificateRefreshKey={certificateRefreshKey}
            onTabChange={onTabChange}
          />
        }
      >
        <Route
          path=""
          element={
            !selectedCourse ? (
              <Welcome hasCourses={courses.length > 0} />
            ) : (
              <EducationProvider
                key={selectedCourse?.ID}
                onCertificateGenerated={handleCertificateGenerated}
              >
                <Education course={selectedCourse} />
              </EducationProvider>
            )
          }
        />
        <Route
          path={RouteStrings.Education.Course}
          element={
            <EducationPage
              courses={courses}
              onCertificateGenerated={handleCertificateGenerated}
            />
          }
        />
        <Route
          path={RouteStrings.Education.Questions}
          element={
            <EducationProvider
              key={selectedCourse?.ID}
              onCertificateGenerated={handleCertificateGenerated}
            >
              <QuestionsPractice course={selectedCourse} />
            </EducationProvider>
          }
        />
        <Route
          path={RouteStrings.Education.Theory}
          element={
            <EducationProvider
              key={selectedCourse?.ID}
              onCertificateGenerated={handleCertificateGenerated}
            >
              <TheoryList course={selectedCourse} userData={user} />
            </EducationProvider>
          }
        />
        <Route
          path={RouteStrings.Education.Practice}
          element={
            <EducationProvider
              key={selectedCourse?.ID}
              onCertificateGenerated={handleCertificateGenerated}
            >
              <PracticeExam />
            </EducationProvider>
          }
        />
        <Route
          path={RouteStrings.Education.Certificate}
          element={
            <CertificatePage
              courses={courses}
              selectedCourse={selectedCourse}
              coursesLoadFailed={coursesError != null}
              userData={user}
              onCertificateGenerated={handleCertificateGenerated}
            />
          }
        />
        <Route
          path={RouteStrings.Education.Documents}
          element={
            <EducationProvider
              key={selectedCourse?.ID}
              onCertificateGenerated={handleCertificateGenerated}
            >
              <DocumentsList course={selectedCourse} />
            </EducationProvider>
          }
        />
      </Route>
    </Routes>
  );

  return (
    <LoadingScreen loading={loading || userLoading || isLoggedIn === null}>
      <div className={style.ontop}>
        <ToastContainer
          position="bottom-left"
          theme="colored"
          transition={Slide}
          pauseOnFocusLoss={false}
          pauseOnHover
          hideProgressBar
          closeOnClick
        />
      </div>
      <div
        className={`${style.app} ${showAppHeader ? style.appWithBrandBar : ""}`}
      >
        <MenuProvider>
          <div className={style.shell}>
            {isLoggedIn && !showAppHeader && !isUiElementsPage && (
              <>
                {userError ? (
                  <ErrorMessage message={userError} onRetry={updateUser} />
                ) : coursesError ? (
                  <ErrorMessage
                    message={coursesError}
                    onRetry={retryFetchCourses}
                  />
                ) : (
                  <Menu
                    availableCourses={courses}
                    initialCourse={selectedCourse}
                    onSelectCourse={handleSelectCourse}
                  />
                )}
              </>
            )}

            <div className={style.content}>
              {showAppHeader ? (
                <>
                  {userError ? (
                    <ErrorMessage message={userError} onRetry={updateUser} />
                  ) : coursesError ? (
                    <ErrorMessage
                      message={coursesError}
                      onRetry={retryFetchCourses}
                    />
                  ) : (
                    <AppShellHeader
                      availableCourses={courses}
                      initialCourse={selectedCourse}
                      onSelectCourse={handleSelectCourse}
                      showAdmin={Boolean(user?.IsAdmin)}
                    />
                  )}
                </>
              ) : null}
              <Suspense fallback={null}>
                <Routes>
              <Route
                path={RouteNames.ReferralLanding}
                element={<ReferralLanding />}
              />
              {IS_UI_ELEMENTS_ENABLED && UIElements ? (
                <Route path={RouteNames.UIElements} element={<UIElements />} />
              ) : null}
              {isLoggedIn ? (
                <>
                  <Route
                    path={RouteNames.Home}
                    element={<Navigate to={RouteNames.Educations} replace />}
                  />
                  <Route
                    path={RouteNames.EducationsBase}
                    element={EducationRoutes}
                  />
                  <Route path={RouteNames.Account} element={<Account />} />
                  <Route
                    path={RouteNames.MyCertificates}
                    element={<MyCertificates availableCourses={courses} />}
                  />
                  <Route
                    path={RouteNames.Admin}
                    element={<Admin userData={user} />}
                  />
                  <Route
                    path={RouteNames.AdminAddEmployees}
                    element={<AddEmployeesView />}
                  />
                  <Route path={RouteNames.Any} element={<NotFound />} />
                  <Route path={RouteNames.NotFound} element={<NotFound />} />
                </>
              ) : (
                <>
                  <Route path={RouteNames.Home} element={<Login />} />
                  <Route path={RouteNames.Any} element={<Login />} />
                </>
              )}
                </Routes>
              </Suspense>
            </div>
          </div>
        </MenuProvider>
      </div>
    </LoadingScreen>
  );
}

export default App;
